/**
 * Utility functions for handling customer impersonation
 * These functions help manage the impersonation process and data refresh
 * without causing authentication issues.
 */

/**
 * Safely navigate to a new path with a full page refresh that preserves authentication
 * 
 * @param {string} path - The path to navigate to
 */
export const navigateWithRefresh = (path) => {
  // console.log(`🔄 ImpersonationUtils: Navigating to ${path} with refresh`);
  
  // Clear any existing impersonation flags to ensure a clean state
  sessionStorage.removeItem('impersonation_loading_shown');
  
  // Set a flag to indicate this is an impersonation navigation
  sessionStorage.setItem('impersonation_navigation', 'true');
  
  // Force a data refresh after navigation completes
  sessionStorage.setItem('force_data_refresh', 'true');
  
  // Store current auth data to be restored after refresh
  const authToken = localStorage.getItem('auth0_access_token');
  const authUser = localStorage.getItem('auth0_user');
  const idToken = localStorage.getItem('auth0_id_token');
  const loginTime = localStorage.getItem('auth0_login_time');
  
  if (authToken && authUser) {
    // console.log('🔐 ImpersonationUtils: Storing auth data for preservation');
    sessionStorage.setItem('temp_auth0_access_token', authToken);
    sessionStorage.setItem('temp_auth0_user', authUser);
    
    if (idToken) {
      sessionStorage.setItem('temp_auth0_id_token', idToken);
    }
    
    if (loginTime) {
      sessionStorage.setItem('temp_auth0_login_time', loginTime);
    }
  } else {
    console.warn('⚠️ ImpersonationUtils: No auth data found to preserve');
  }
  
  // Show loading overlay before navigation
  showLoadingOverlay();
  
  // Navigate to the new path after a short delay to ensure overlay is shown
  setTimeout(() => {
    window.location.href = path;
  }, 50);
};

/**
 * Shows a full-screen loading overlay during impersonation navigation
 */
function showLoadingOverlay() {
  // Create overlay container
  const overlay = document.createElement('div');
  overlay.id = 'impersonation-loading-overlay';
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100%';
  overlay.style.height = '100%';
  overlay.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
  overlay.style.display = 'flex';
  overlay.style.flexDirection = 'column';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.zIndex = '9999';
  
  // Create spinner
  const spinner = document.createElement('div');
  spinner.style.width = '50px';
  spinner.style.height = '50px';
  spinner.style.border = '5px solid #f3f3f3';
  spinner.style.borderTop = '5px solid #3498db';
  spinner.style.borderRadius = '50%';
  spinner.style.animation = 'spin 1s linear infinite';
  
  // Create animation style
  const style = document.createElement('style');
  style.textContent = '@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';
  
  // Create message
  const message = document.createElement('p');
  message.textContent = 'Changing customer context...';
  message.style.marginTop = '20px';
  message.style.fontSize = '18px';
  message.style.fontWeight = 'bold';
  message.style.color = '#333';
  
  // Add elements to DOM
  overlay.appendChild(spinner);
  overlay.appendChild(message);
  document.head.appendChild(style);
  document.body.appendChild(overlay);
  
  // Store in sessionStorage that we've shown the overlay
  sessionStorage.setItem('impersonation_loading_shown', 'true');
};

/**
 * Check if we need to restore authentication after a refresh
 * This should be called early in the app initialization
 * 
 * @returns {boolean} - Whether authentication was restored
 */
export const checkAndRestoreAuth = () => {
  const isImpersonationNavigation = sessionStorage.getItem('impersonation_navigation') === 'true';
  
  if (isImpersonationNavigation) {
    // console.log('🔄 ImpersonationUtils: Detected impersonation navigation');
    
    // Clear the flag
    sessionStorage.removeItem('impersonation_navigation');
    
    // Check if we have stored auth data
    const tempAuthToken = sessionStorage.getItem('temp_auth0_access_token');
    const tempAuthUser = sessionStorage.getItem('temp_auth0_user');
    
    if (tempAuthToken && tempAuthUser) {
      // console.log('🔐 ImpersonationUtils: Restoring auth data');
      
      // Restore the auth data to localStorage
      localStorage.setItem('auth0_access_token', tempAuthToken);
      localStorage.setItem('auth0_user', tempAuthUser);
      
      const tempIdToken = sessionStorage.getItem('temp_auth0_id_token');
      if (tempIdToken) {
        localStorage.setItem('auth0_id_token', tempIdToken);
      }
      
      const tempLoginTime = sessionStorage.getItem('temp_auth0_login_time');
      if (tempLoginTime) {
        localStorage.setItem('auth0_login_time', tempLoginTime);
      }
      
      // Clear the temporary storage
      sessionStorage.removeItem('temp_auth0_access_token');
      sessionStorage.removeItem('temp_auth0_user');
      sessionStorage.removeItem('temp_auth0_id_token');
      sessionStorage.removeItem('temp_auth0_login_time');
      
      // Ensure we'll force a data refresh after auth is restored
      if (sessionStorage.getItem('force_data_refresh') !== 'true') {
        sessionStorage.setItem('force_data_refresh', 'true');
      }
      
      return true;
    } else {
      console.warn('⚠️ ImpersonationUtils: No stored auth data found');
    }
  }
  
  return false;
};

/**
 * Show a loading overlay during app initialization if we're coming from an impersonation
 * This should be called in App.jsx
 * 
 * @returns {boolean} - Whether the loading overlay was shown
 */
export const showInitialLoadingIfNeeded = () => {
  // Check if we're in an impersonation navigation
  const isImpersonationNavigation = sessionStorage.getItem('impersonation_navigation') === 'true';
  const loadingWasShown = sessionStorage.getItem('impersonation_loading_shown') === 'true';
  
  if (isImpersonationNavigation || loadingWasShown) {
    // Create a loading overlay that matches the one shown before navigation
    const overlay = document.createElement('div');
    overlay.id = 'impersonation-loading-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.zIndex = '9999';
    
    // Create spinner
    const spinner = document.createElement('div');
    spinner.style.width = '50px';
    spinner.style.height = '50px';
    spinner.style.border = '5px solid #f3f3f3';
    spinner.style.borderTop = '5px solid #3498db';
    spinner.style.borderRadius = '50%';
    spinner.style.animation = 'spin 1s linear infinite';
    
    // Create animation style
    const style = document.createElement('style');
    style.textContent = '@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';
    
    // Create message
    const message = document.createElement('p');
    message.textContent = 'Changing customer context...';
    message.style.marginTop = '20px';
    message.style.fontSize = '18px';
    message.style.fontWeight = 'bold';
    message.style.color = '#333';
    
    // Add elements to DOM
    overlay.appendChild(spinner);
    overlay.appendChild(message);
    document.head.appendChild(style);
    document.body.appendChild(overlay);
    
    // Clear the flag
    sessionStorage.removeItem('impersonation_loading_shown');
    
    // Return true to indicate we showed the overlay
    return true;
  }
  
  return false;
};

/**
 * Remove the loading overlay when the app is ready
 */
export const removeLoadingOverlay = () => {
  const overlay = document.getElementById('impersonation-loading-overlay');
  if (overlay) {
    // Fade out the overlay
    overlay.style.transition = 'opacity 0.5s';
    overlay.style.opacity = '0';
    
    // Remove after animation
    setTimeout(() => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    }, 500);
    
    // Always clear the loading shown flag when removing the overlay
    sessionStorage.removeItem('impersonation_loading_shown');
  }
};
